import UpgradePlanModal from '@/components/Modals/UpgradePlanModal';
import expressPreviewVideo from '@/images/expressPreviewVideo.mp4';
import premiumPlusPreviewVideo from '@/images/premiumPlusPreviewVideo.mp4';
import Presenters from '@/images/SPPlus.png';
import { AvatarType } from '@/pages/avatar/common/types';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useCreateAvatarStore } from '../../common/context';
import { StepTitle } from '../Styled/Title';
import { SelectableBox } from './SelectableBox';

const Title = styled(StepTitle)`
    margin-bottom: 24px;
    text-align: center;
    margin-block-start: 40px;
    margin-block-end: 25px;
`;

const Container = styled.div`
    display: flex;
    flex-direction: row;
    padding: 32px 24px 40px;
    gap: 24px;
    align-items: center;
    margin: 0 auto;
    width: 100%;
    max-width: 1000px;
`;

const LeftSide = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 50%;
    align-items: center;
`;

const VideoContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: flex-start;
    width: 50%;
    height: auto;
`;

const StyledVideo = styled.video`
    max-width: 605px;
    height: 600px;
    object-fit: contain;
    border-radius: 22px;
    margin-inline-start: 130px;
`;

const videoSources: { [key: string]: string } = {
    [AvatarType.Express]: expressPreviewVideo,
    [AvatarType.PremiumPlus]: premiumPlusPreviewVideo,
};

export const ChooseVideoAvatar = observer(() => {
    const createAvatarStore = useCreateAvatarStore();
    const [selectedBox, setSelectedBox] = useState<AvatarType.Express | AvatarType.PremiumPlus>(
        createAvatarStore.type || AvatarType.Express
    );
    const handleBoxClick = (boxType: AvatarType.Express | AvatarType.PremiumPlus) => {
        setSelectedBox(boxType);
        createAvatarStore.type = boxType;
    };
    return (
        <>
            <Title>Choose your Avatar</Title>
            <Container>
                <LeftSide>
                    <SelectableBox
                        description=""
                        id={AvatarType.Express}
                        testId="express-avatar-radio-box"
                        title="Express"
                        features={[
                            { text: 'High quality', isSupported: true, shouldHaveInfoCircle: false },
                            {
                                text: 'Customizable background',
                                isSupported: true,
                                shouldHaveInfoCircle: true,
                                tooltipDescription: "Upload your footage with a green screen to customize the Avatar's background.",
                            },
                            {
                                text: 'Streaming not supported',
                                isSupported: false,
                                shouldHaveInfoCircle: true,
                                tooltipDescription: 'Express Avatars are not available on Agents.',
                            },
                        ]}
                        isSelected={selectedBox === AvatarType.Express}
                        onClick={() => handleBoxClick(AvatarType.Express)}
                    />
                    <SelectableBox
                        description=""
                        id={AvatarType.PremiumPlus}
                        data-testid="premium-plus-avatar-radio-box"
                        title="Premium+"
                        features={[
                            { text: 'High quality', isSupported: true, shouldHaveInfoCircle: false },
                            {
                                text: 'Customizable background',
                                isSupported: true,
                                shouldHaveInfoCircle: true,
                                tooltipDescription: "Upload your footage with a green screen to customize the Avatar's background.",
                            },
                            {
                                text: 'Streaming supported',
                                isSupported: true,
                                shouldHaveInfoCircle: true,
                                tooltipDescription: 'Premium+ Avatars can be used with Agents.',
                            },
                        ]}
                        isSelected={selectedBox === AvatarType.PremiumPlus}
                        onClick={() => handleBoxClick(AvatarType.PremiumPlus)}
                    />
                </LeftSide>

                <VideoContainer>
                    <StyledVideo src={videoSources[selectedBox]} autoPlay muted loop />
                </VideoContainer>

                <UpgradePlanModal
                    open={createAvatarStore.upgradeMinutesPopupOpen === true}
                    onClose={() => createAvatarStore.setUpgradeMinutesPopupOpen(false)}
                    title={<div>Get our most realistic Avatars</div>}
                    imageFile={Presenters}
                    ctaText={'Upgrade now'}
                    from={'choose-avatar-page'}
                    features={[
                        'Unlimited video creation',
                        'Longer videos',
                        'Express avatars',
                        'Premium voices',
                        'Voice clones',
                        'Embedded agents',
                    ]}
                    subheading="Our plan offer"
                />
            </Container>
        </>
    );
});
